// Il cruscotto di un impianto, sul modello della scheda adempimenti in Excel dei clienti:
// quadro strumenti, adempimenti raggruppati per ente, produzione contro l'anno prima.
import { Link, useParams } from 'react-router-dom'
import { ENTE_SCADENZA, etichettaPeriodicita, titoloScadenza, type EnteScadenza, type Periodo, type StatoDichiarazione } from '@praticheenergia/domain'
import { dec } from '@praticheenergia/ad1-engine'
import { useDatiQuadro } from '../home/useDatiQuadro'
import { Lancetta, Spia, Stato, ZONE_GIORNI } from '../home/Strumenti'
import {
  TONO_SCADENZA, confrontoMensile, dataAvviso, dataItaliana, giorniResidui, produzioneImpianto,
  righeAdempimenti, righeDichiarazioni, spiaDichiarazioni, testoResidui, type RigaTabella,
} from '../home/quadro'
import { valutaScostamento } from '../green/scostamento'
import { MESI, kwh, percento } from '../green/formato'
import { SchedaImpianto } from './SchedaImpianto'
import { ANNO_IMPOSTA } from '../lib/anno-imposta'

const ENTI: readonly EnteScadenza[] = ['DSO', 'ADM', 'GSE', 'ARERA', 'Altro']

// Solo per leggere: l'importo resta la stringa del numeric, Intl la formatta senza calcoli.
const euro = (v: string) => `${Number(v).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`

export function QuadroImpianto(props: {
  impiantoId: number
  dichiarazioni: readonly { periodo: Periodo; stato: StatoDichiarazione }[]
}) {
  const { impiantoId, dichiarazioni } = props
  const idPartner = Number(useParams().idPartner)
  const base = `/cliente/${idPartner}`
  const tutti = useDatiQuadro(idPartner)
  // ponytail: filtra in memoria le scadenze di tutto il partner (cache condivisa con lo
  // scadenzario); se diventano migliaia, una query per impianto.
  const scadenze = tutti.scadenze.filter((s) => s.impiantoId === impiantoId)
  const green = tutti.green.find((g) => g.impiantoId === impiantoId)

  const produzione = produzioneImpianto(green, tutti.soglia)
  const dich = spiaDichiarazioni(dichiarazioni)
  const soglia = green?.soglia ?? tutti.soglia
  const scostamento = green ? valutaScostamento(green.serie, soglia) : null
  const variazione = scostamento?.variazioneMese ?? null
  const mesi = green ? confrontoMensile(green.serie, soglia) : []
  const massimo = mesi.reduce((m, x) => Math.max(m, Number(x.attuale ?? 0), Number(x.precedente ?? 0)), 0)
  // Scadenze e dichiarazioni nella stessa tabella, ordinate per ente e poi per data.
  const ordinate: RigaTabella[] = [
    ...righeAdempimenti(scadenze).map(({ scadenza: s, ultimoRinnovo }) => ({
      chiave: `s-${s.id}`,
      ente: ENTE_SCADENZA[s.tipo],
      titolo: titoloScadenza(s),
      nota: [s.note, s.importo && euro(s.importo)].filter(Boolean).join(' · '),
      periodicita: etichettaPeriodicita(s.periodicitaMesi),
      ultimoRinnovo,
      scadenza: s.scadenza,
      tono: TONO_SCADENZA[s.stato],
      residui: s.completataIl ? `Fatto il ${dataItaliana(s.completataIl)}` : testoResidui(giorniResidui(s.scadenza)),
      avviso: s.completataIl ? null : dataAvviso(s.scadenza, s.giorniPreavviso),
      preavviso: s.giorniPreavviso,
    })),
    ...righeDichiarazioni(dichiarazioni, tutti.termini, ANNO_IMPOSTA),
  ].sort(
    (a, b) =>
      ENTI.indexOf(a.ente as EnteScadenza) - ENTI.indexOf(b.ente as EnteScadenza) ||
      (a.scadenza ?? '9999').localeCompare(b.scadenza ?? '9999'),
  )
  // Spie e lancetta contano le stesse righe della tabella: una dichiarazione oltre il
  // termine accende «Scaduti» come una scadenza.
  const scadute = ordinate.filter((r) => r.tono === 'rosso').length
  const inPreavviso = ordinate.filter((r) => r.tono === 'ambra').length
  const prossima = ordinate
    .filter((r) => (r.tono === 'ambra' || r.tono === 'verde') && r.scadenza !== null)
    .sort((a, b) => a.scadenza!.localeCompare(b.scadenza!))[0]
  const giorni = prossima ? giorniResidui(prossima.scadenza!) : null

  return (
    <div className="cruscotto">
      <section className="quadro" aria-label="Quadro strumenti dell'impianto">
        <Lancetta
          valore={giorni}
          min={0}
          max={120}
          zone={ZONE_GIORNI}
          testo={giorni === null ? '—' : `${giorni} gg`}
          etichetta="al prossimo adempimento"
          dettaglio={prossima ? `${prossima.titolo} · ${dataItaliana(prossima.scadenza!)}` : 'Nessun adempimento in calendario'}
        />
        <div className="quadro-spie">
          <Spia tono={scadute > 0 ? 'rosso' : null} icona="avviso" valore={scadute} etichetta="Scaduti" />
          <Spia tono={inPreavviso > 0 ? 'ambra' : null} icona="orologio" valore={inPreavviso} etichetta="In preavviso" />
          <Spia
            tono={produzione.anomalia ? 'rosso' : null}
            icona="impianti"
            valore={produzione.anomalia ? percento(variazione) : '—'}
            etichetta={produzione.anomalia ? 'Produzione sotto soglia' : `Produzione: ${produzione.testo.toLowerCase()}`}
          />
          <Spia tono={dich.tono} icona="documento" valore={dich.testo} etichetta="Dichiarazioni" />
        </div>
        <Lancetta
          valore={variazione === null ? null : 100 + Number(variazione)}
          min={60}
          max={120}
          zone={[{ fino: 100 - Number(soglia), tono: 'rosso' }, { fino: 120, tono: 'verde' }]}
          testo={variazione === null ? '—' : `${dec(100).plus(variazione).toFixed(0)}%`}
          etichetta={scostamento?.ultimoMese ? `produzione di ${MESI[scostamento.ultimoMese.mese - 1]}` : 'produzione'}
          dettaglio={variazione === null ? produzione.testo : `rispetto all'anno prima · soglia ${100 - Number(soglia)}%`}
        />
      </section>

      <SchedaImpianto impiantoId={impiantoId} />

      <section className="riquadro" aria-labelledby="adempimenti">
        <h2 id="adempimenti">Scadenzario adempimenti</h2>
        <p className="riquadro-sottotitolo">Verde in regola · ambra dentro il preavviso · rosso scaduto</p>
        {ordinate.length === 0 ? (
          <p>
            Nessun adempimento per questo impianto. <Link to={`${base}/scadenzario`}>Aggiungilo dallo scadenzario</Link>
          </p>
        ) : (
          <div className="tabella-scorre">
            <table className="tabella-quadro">
              <thead>
                <tr>
                  <th>Ente</th>
                  <th>Adempimento</th>
                  <th>Periodicità</th>
                  <th>Ultimo rinnovo</th>
                  <th>Scadenza</th>
                  <th>Residui</th>
                  <th>Avviso dal</th>
                </tr>
              </thead>
              <tbody>
                {ordinate.map((r) => (
                  <tr key={r.chiave} className={`riga-${r.tono}`}>
                    <td><span className={`ente ente-${r.ente.toLowerCase()}`}>{r.ente}</span></td>
                    <td>
                      <strong>{r.titolo}</strong>
                      {r.nota && <small>{r.nota}</small>}
                    </td>
                    <td>{r.periodicita}</td>
                    <td>{r.ultimoRinnovo ? dataItaliana(r.ultimoRinnovo) : '—'}</td>
                    <td>{r.scadenza ? dataItaliana(r.scadenza) : '—'}</td>
                    <td><Stato tono={r.tono}>{r.residui}</Stato></td>
                    <td>
                      {r.avviso ? dataItaliana(r.avviso) : '—'}
                      {r.preavviso !== null && <small>preavviso {r.preavviso} gg</small>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Link to={`${base}/scadenzario`}>Gestisci nello scadenzario</Link>
      </section>

      {mesi.length > 0 && (
        <section className="riquadro" aria-labelledby="produzione">
          <h2 id="produzione">Produzione mensile</h2>
          <p className="riquadro-sottotitolo">
            Ultimi 12 mesi contro lo stesso mese dell'anno prima · in arancio i mesi sotto soglia
          </p>
          <div className="tabella-scorre">
            <ul className="barre" aria-label="Produzione mensile">
              {mesi.map((m) => (
                <li key={`${m.anno}-${m.mese}`}>
                  <span className="barre-coppia">
                    <span
                      className="barra-precedente"
                      style={{ height: `${massimo ? (Number(m.precedente ?? 0) / massimo) * 100 : 0}%` }}
                      title={`${MESI[m.mese - 1]} ${m.anno - 1}: ${kwh(m.precedente)}`}
                    />
                    <span
                      className={m.sotto ? 'barra-attuale sotto' : 'barra-attuale'}
                      style={{ height: `${massimo ? (Number(m.attuale ?? 0) / massimo) * 100 : 0}%` }}
                      title={`${MESI[m.mese - 1]} ${m.anno}: ${kwh(m.attuale)}`}
                    />
                  </span>
                  <span className="barre-mese">{MESI[m.mese - 1]}</span>
                  <span className="solo-lettori">
                    {kwh(m.attuale)} contro {kwh(m.precedente)} dell'anno prima{m.sotto ? ', sotto soglia' : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          {green && <Link to={`${base}/impianti-green/${green.id}`}>Dettaglio della produzione</Link>}
        </section>
      )}
    </div>
  )
}
